import { korusOrder } from "../../data/korusOrder";
import { logger } from "../../lib/logger";

// Format date to YYYY-MM-DD for input elements
export const formatDateForInput = (date) => {
  const parsed = new Date(date);
  // Guard against invalid dates (e.g. a corrupt localStorage due-date value):
  // toISOString() throws RangeError on an Invalid Date, which would crash the
  // entire Flashcards page. Fall back to today, matching loadState's default.
  const d = isNaN(parsed.getTime()) ? new Date() : parsed;
  const localDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return localDate.toISOString().split("T")[0];
};

// Convert date string to BCE/CE format
export const formatDateDisplay = (dateStr) => {
  // Guard against missing/non-string dates. Flashcard decks are rehydrated from
  // localStorage (loadState), so a corrupt or legacy card can have a null/absent
  // date; calling .split() on it throws and crashes the card render. An empty
  // string would also format to a stray " CE", so treat all falsy input the same.
  if (!dateStr || typeof dateStr !== "string") return "—";
  const parts = dateStr.split("/");
  if (parts.length === 2) {
    const start = parts[0].startsWith("-")
      ? parts[0].slice(1) + " BCE"
      : parts[0] + " CE";
    const end = parts[1].startsWith("-")
      ? parts[1].slice(1) + " BCE"
      : parts[1] + " CE";
    return `${start} - ${end}`;
  }
  return parts[0].startsWith("-")
    ? parts[0].slice(1) + " BCE"
    : parts[0] + " CE";
};

// Get the current academic year start (September)
// Academic year runs Sep-May, so:
// - If current month is Sep-Dec, academic year started this calendar year
// - If current month is Jan-Aug, academic year started last calendar year
export const getCurrentAcademicYear = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed (0 = Jan, 8 = Sep)
  const year = now.getFullYear();

  // If September (8) through December (11), we're in fall of current year
  // If January (0) through August (7), academic year started previous year
  return month >= 8 ? year : year - 1;
};

// Convert month-day string to full date with correct academic year
// Format: "M-D" (e.g., "9-3" for September 3)
export const getAcademicDate = (monthDayStr) => {
  if (typeof monthDayStr !== "string") return null;
  const parts = monthDayStr.split("-");
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  // transformDueDates emits "" for malformed Sanity dates; bail before those
  // become an Invalid Date that silently drops the card from the due deck
  // (Invalid Date <= date is always false in getCardsDueByDate).
  if (Number.isNaN(month) || Number.isNaN(day)) return null;

  const academicYearStart = getCurrentAcademicYear();

  // Fall semester (Sep-Dec) uses the academic year start
  // Spring semester (Jan-May) uses academic year start + 1
  const year = month >= 9 ? academicYearStart : academicYearStart + 1;

  return new Date(year, month - 1, day); // month is 0-indexed in Date
};

// Create due dates map from assignments data
// Now handles month-day format and calculates year automatically
export const createDueDatesMap = (dueDatesData) => {
  const map = new Map();
  dueDatesData.assignments.forEach((assignment) => {
    const id = parseInt(assignment.id, 10);
    if (!isNaN(id)) {
      const date = getAcademicDate(assignment.dueDate);
      if (date) map.set(id, date);
    }
  });
  return map;
};

// Get card IDs that are due by a specific date
export const getCardsDueByDate = (dueDatesMap, date) => {
  const cardIds = [];
  dueDatesMap.forEach((dueDate, id) => {
    if (dueDate <= date) {
      cardIds.push(id);
    }
  });
  return cardIds;
};

// Filter cards to only those in the Korus teaching order
export const filterByKorusOrder = (cardIds) => {
  const cardIdsSet = new Set(cardIds);
  return korusOrder.filter((id) => cardIdsSet.has(id));
};

interface BuildDeckOptions {
  dueDatesMap?: Map<number, Date>;
  dueByDate?: Date;
  selectedUnits?: number[];
  shouldShuffle?: boolean;
  useAllCards?: boolean;
}

export const buildDeck = (artworks: unknown[], options: BuildDeckOptions = {}) => {
  const {
    dueDatesMap,
    dueByDate = new Date(),
    selectedUnits = [],
    shouldShuffle = false,
    useAllCards = false,
  } = options;

  let cardsInOrder;

  if (useAllCards) {
    // Use all cards in Korus teaching order
    cardsInOrder = [...korusOrder];
  } else {
    // Get cards due by date and filter to Korus order
    const cardsDueByDate = getCardsDueByDate(dueDatesMap, dueByDate);
    cardsInOrder = filterByKorusOrder(cardsDueByDate);
  }

  // Create artwork lookup map
  const artworksById = new Map(artworks.map((a: any) => [a.id, a]));

  // Build deck in Korus order, filtered by units
  let deck = [];
  for (const id of cardsInOrder) {
    const artwork = artworksById.get(id);
    if (artwork && (selectedUnits.length === 0 || selectedUnits.includes(artwork.unit))) {
      deck.push(artwork);
    }
  }

  // Shuffle if requested
  if (shouldShuffle) {
    deck = shuffleArray([...deck]);
  }

  return deck;
};

// Fisher-Yates shuffle
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// LocalStorage keys
const STORAGE_KEYS = {
  deck: "flashcards_deck",
  currentCard: "flashcards_currentCard",
  selectedUnits: "flashcards_selectedUnits",
  dueDate: "flashcards_displayCardsDueBy",
  isShuffled: "flashcards_isShuffled",
  deckMode: "flashcards_deckMode",
  hasSeenInstructions: "flashcards_hasSeenSwipeInstructions",
  hasSeenFlipHint: "flashcards_hasSeenFlipHint",
  dataVersion: "flashcards_dataVersion",
};

// Current data version - increment when data source changes (e.g., Sanity migration)
const CURRENT_DATA_VERSION = 2;

// Save flashcard state to localStorage
export const saveState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEYS.deck, JSON.stringify(state.deck));
    localStorage.setItem(STORAGE_KEYS.currentCard, state.currentCard.toString());
    localStorage.setItem(STORAGE_KEYS.selectedUnits, JSON.stringify(state.selectedUnits));
    localStorage.setItem(STORAGE_KEYS.dueDate, state.dueDate.toISOString());
    localStorage.setItem(STORAGE_KEYS.isShuffled, JSON.stringify(state.isShuffled));
    localStorage.setItem(STORAGE_KEYS.deckMode, state.deckMode || "korus");
    localStorage.setItem(STORAGE_KEYS.dataVersion, String(CURRENT_DATA_VERSION));
  } catch (error) {
    logger.error("Error saving flashcard state:", error);
  }
};

// Load flashcard state from localStorage
export const loadState = () => {
  try {
    // Check data version - if outdated, invalidate cache
    const storedVersion = parseInt(localStorage.getItem(STORAGE_KEYS.dataVersion) || "0", 10);
    if (storedVersion < CURRENT_DATA_VERSION) {
      // Clear outdated deck data (images now come from Sanity CDN)
      localStorage.removeItem(STORAGE_KEYS.deck);
      localStorage.removeItem(STORAGE_KEYS.currentCard);
      localStorage.setItem(STORAGE_KEYS.dataVersion, String(CURRENT_DATA_VERSION));
      return null; // Force fresh deck rebuild
    }

    const deck = JSON.parse(localStorage.getItem(STORAGE_KEYS.deck) || "[]");
    const currentCard = parseInt(localStorage.getItem(STORAGE_KEYS.currentCard) || "0", 10);
    const rawSelectedUnits = JSON.parse(localStorage.getItem(STORAGE_KEYS.selectedUnits) || "[]");
    const dueDateStr = localStorage.getItem(STORAGE_KEYS.dueDate);
    const dueDate = dueDateStr ? new Date(dueDateStr) : new Date();
    const isShuffled = JSON.parse(localStorage.getItem(STORAGE_KEYS.isShuffled) || "false");
    const deckMode = localStorage.getItem(STORAGE_KEYS.deckMode) || "korus";

    // Ensure selectedUnits are numbers (in case they were stored as strings)
    const selectedUnits = Array.isArray(rawSelectedUnits)
      ? rawSelectedUnits.map((u) => (typeof u === 'string' ? parseInt(u, 10) : u)).filter((u) => !isNaN(u))
      : [];

    return {
      deck: Array.isArray(deck) ? deck : [],
      currentCard: isNaN(currentCard) ? 0 : currentCard,
      selectedUnits,
      dueDate,
      isShuffled,
      deckMode,
    };
  } catch (error) {
    logger.error("Error loading flashcard state:", error);
    return null;
  }
};

// Clear deck from localStorage (keep preferences)
export const clearDeckStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.deck);
  localStorage.removeItem(STORAGE_KEYS.currentCard);
};

// Check if user has seen swipe instructions
export const hasSeenSwipeInstructions = () => {
  return localStorage.getItem(STORAGE_KEYS.hasSeenInstructions) === "true";
};

// Mark swipe instructions as seen
export const markSwipeInstructionsSeen = () => {
  localStorage.setItem(STORAGE_KEYS.hasSeenInstructions, "true");
};

// Check if user has seen flip hint
export const hasSeenFlipHint = () => {
  return localStorage.getItem(STORAGE_KEYS.hasSeenFlipHint) === "true";
};

// Mark flip hint as seen
export const markFlipHintSeen = () => {
  localStorage.setItem(STORAGE_KEYS.hasSeenFlipHint, "true");
};
